// Stores
import { useCustomFieldStore } from "@/stores/custom-field.store";
import { useOrderStore } from "@smeetz/order-store";

// Helpers
import { dexieDB } from "@/services/dexie-database";
import { newOperationContext } from "@/services/package-logger";
import { generateID } from "@/utils/helpers/app";
import { nowDB } from "../helpers/dates";
import { IFileRef } from "@/types/db/answer-sync";

// Types
import {
  CustomFieldKind,
  CustomFieldsData,
  CustomFieldAnswerDto,
  CreateCustomFieldAnswerDto,
  SyncCustomFieldAnswerDto,
} from "@smeetz/custom-field-common";
import { CustomFieldAnswer, LineType } from "@smeetz/orders";
import { CustomFieldAnswerType } from "@smeetz/order-common";
import {
  CustomFieldType,
  QuestionType,
  ICustomFieldAnswer,
  ICustomField,
} from "@/types/db/product-category";

type CustomFieldDefinition = CustomFieldsData["customFields"][number];

interface OrderCustomFieldAnswers {
  booking: ICustomFieldAnswer[];
  ticket: ICustomFieldAnswer[];
}

function normalizeDateValue(value: Date | string | null | undefined): string {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "string" && value) {
    return value;
  }

  return nowDB();
}

/**
 * Resolves the cancelled flag from a CustomFieldDefinition.
 *
 * @param {CustomFieldDefinition} field
 * @returns {number}
 */
function resolveCancelledFlag(field: CustomFieldDefinition): number {
  const candidate =
    (field as unknown as { canceled?: boolean; cancelled?: boolean }) || {};
  if (typeof candidate.canceled === "boolean") {
    return candidate.canceled ? 1 : 0;
  }
  if (typeof candidate.cancelled === "boolean") {
    return candidate.cancelled ? 1 : 0;
  }
  return 0;
}

/**
 * Resolves the visibleFor property from a CustomFieldDefinition.
 *
 * @param {CustomFieldDefinition} field
 * @returns {number}
 */
function resolveVisibleFor(field: CustomFieldDefinition): number {
  const value = (field as unknown as { visibleFor?: number }).visibleFor;
  return typeof value === "number" ? value : 0;
}

/**
 * Maps a single CustomFieldDefinition to ICustomField for the legacy CustomFields modal.
 *
 * @param {CustomFieldDefinition} field
 * @param {number} productId
 * @param {CustomFieldType} type
 * @returns {ICustomField}
 */
function toLegacyCustomField(
  field: CustomFieldDefinition,
  productId: number,
  type: CustomFieldType
): ICustomField {
  return {
    id: String(field.fieldId),
    productId,
    name: field.label,
    answerExample: field.answerExample || "",
    position: field.position,
    cancelled: resolveCancelledFlag(field),
    questionType: field.questionType,
    informationType: field.informationType,
    list: field.list || [],
    validation: field.validation ? 1 : 0,
    visibleFor: resolveVisibleFor(field),
    customFieldType: type,
  };
}

/**
 * Maps CustomFieldsData from the new structure to ICustomFieldAnswer for the legacy CustomFields modal.
 * Creates empty answer objects ready to be filled by the user.
 *
 * @param {CustomFieldsData[]} customFieldsDataList - Array of custom fields data from the new structure
 * @param {string} bookingUuid - Optional booking UUID to attach to booking-level fields
 * @param {string} pssUuid - Optional PSS UUID to attach to ticket-level fields
 * @returns {ICustomFieldAnswer[]} Array of ICustomFieldAnswer objects ready for the CustomFields modal
 */
export function mapToCustomFieldAnswers(
  customFieldsDataList: CustomFieldsData[],
  bookingUuid?: string,
  pssUuid?: string
): ICustomFieldAnswer[] {
  const now = nowDB();
  const orderStore = useOrderStore();

  return customFieldsDataList.flatMap((customFieldsData) =>
    customFieldsData.customFields.map((field) => {
      const customField: ICustomField = {
        id: String(field.fieldId),
        productId: customFieldsData.productId,
        name: field.label,
        answerExample: field.answerExample || "",
        position: field.position,
        cancelled: 0,
        questionType: field.questionType,
        informationType: field.informationType,
        list: field.list || [],
        validation: field.validation ? 1 : 0,
        visibleFor: field.visibleFor || 0,
        customFieldType:
          field.kind === CustomFieldKind.Order
            ? CustomFieldType.BOOKING
            : CustomFieldType.TICKET,
      };

      const answer: ICustomFieldAnswer = {
        uuid: generateID(),
        bookingUuid: orderStore.selectedOrder?.id,
        pssUuids: orderStore.selectedOrder?.orderSession?.orderLines?.map(
          (line) => line.id
        ),
        answer: "",
        listAnswer: [],
        createdAt: now,
        updatedAt: now,
        customField,
      };

      if (field.kind === CustomFieldKind.Order && bookingUuid) {
        answer.bookingUuid = bookingUuid;
      } else if (field.kind === CustomFieldKind.OrderLine && pssUuid) {
        answer.pssUuid = pssUuid;
      }

      return answer;
    })
  );
}

/**
 * Converts ICustomFieldAnswer array to CustomFieldAnswerDto array for API validation.
 * Transforms the legacy custom field answer format to the format expected by the custom field service.
 *
 * @param {ICustomFieldAnswer[]} customFieldAnswers - Array of custom field answers from the legacy structure
 * @returns {CustomFieldAnswerDto[]} Array of CustomFieldAnswerDto objects ready for validation
 */
export function mapToCustomFieldAnswerDtos(
  customFieldAnswers: ICustomFieldAnswer[]
): CustomFieldAnswerDto[] {
  return customFieldAnswers.map((answer) => {
    let answerValue = answer.answer;

    // If listAnswer is an array (checkboxes), use it
    if (Array.isArray(answer.listAnswer) && answer.listAnswer.length > 0) {
      answerValue = answer.listAnswer.join(",");
    } else if (answer.listAnswer && !Array.isArray(answer.listAnswer)) {
      // If listAnswer is a single value (dropdown/radio), use it
      answerValue = answer.listAnswer as string;
    }

    return {
      fieldId: String(answer.customField.id),
      answer: answerValue,
    };
  });
}

export function mapToCreateCustomFieldAnswerDto(
  customFieldAnswers: ICustomFieldAnswer
): CreateCustomFieldAnswerDto {
  const orderStore = useOrderStore();
  let answerValue = customFieldAnswers.answer;

  // If listAnswer is an array (checkboxes), use it
  if (
    Array.isArray(customFieldAnswers.listAnswer) &&
    customFieldAnswers.listAnswer.length > 0
  ) {
    answerValue = customFieldAnswers.listAnswer.join(",");
  } else if (
    customFieldAnswers.listAnswer &&
    !Array.isArray(customFieldAnswers.listAnswer)
  ) {
    // If listAnswer is a single value (dropdown/radio), use it
    answerValue = customFieldAnswers.listAnswer as string;
  }

  // Find orderLineId from unitItemId (pssUuid)
  let orderLineId: string | undefined;
  if (customFieldAnswers.pssUuid) {
    const orderLine = orderStore.allOrderLines.find((line) =>
      line.unitItems?.some(
        (unitItem) => unitItem.id === customFieldAnswers.pssUuid
      )
    );
    orderLineId = orderLine?.id;
  }

  const fileInfo =
    customFieldAnswers.customField.questionType === QuestionType.FILE &&
    customFieldAnswers.fileID
      ? {
          id: customFieldAnswers.fileID,
          name: customFieldAnswers.answer,
        }
      : undefined;

  return {
    fieldId: String(customFieldAnswers.customField.id),
    unitItemId: customFieldAnswers.pssUuid || undefined,
    orderLineId: orderLineId,
    answer: answerValue,
    id: customFieldAnswers.customFieldAnswerId || undefined,
    ...(fileInfo ? { file: fileInfo } : {}),
  };
}

/**
 * Get all custom field answers for a specific order, both booking and ticket level.
 *
 * @param {string} orderId
 * @returns {Promise<OrderCustomFieldAnswers>}
 */
export async function getOrderCustomFieldAnswers(
  orderId: string
): Promise<OrderCustomFieldAnswers> {
  return buildOrderCustomFieldAnswers(orderId);
}

/**
 * Builds custom field answers for a specific order.
 *
 * @param {string} orderId
 * @returns {Promise<OrderCustomFieldAnswers>}
 */
async function buildOrderCustomFieldAnswers(
  orderId: string
): Promise<OrderCustomFieldAnswers> {
  const customFieldStore = useCustomFieldStore();
  const orderStore = useOrderStore();

  const selectedOrder = orderStore.selectedOrder;

  if (!selectedOrder) {
    throw new Error("No order selected");
  }

  if (selectedOrder.id !== orderId) {
    throw new Error(
      `Selected order (${selectedOrder.id}) does not match requested order (${orderId})`
    );
  }

  const customFieldsData = await customFieldStore.fetchOrderCustomFields(
    orderId
  );

  if (!customFieldsData.length) {
    return { booking: [], ticket: [] };
  }

  const now = nowDB();
  const bookingAnswers: ICustomFieldAnswer[] = [];
  const ticketAnswers: ICustomFieldAnswer[] = [];

  const orderLines = selectedOrder.orderSession.orderLines || [];
  const orderLineIds = orderLines.map((line) => line.id);

  const orderLinesByCategoryId = new Map<string, typeof orderLines>();

  orderLines.forEach((line) => {
    const key = String(line.categoryId);
    const entries = orderLinesByCategoryId.get(key);
    if (entries) {
      entries.push(line);
    } else {
      orderLinesByCategoryId.set(key, [line]);
    }
  });

  const ensuredOrderLineIds = new Set<string>();

  const getUnitItemsForLine = async (lineId: string) => {
    if (!ensuredOrderLineIds.has(lineId)) {
      await ensureUnitItemsForOrderLine(lineId);
      ensuredOrderLineIds.add(lineId);
    }

    const refreshedLine =
      orderStore.selectedOrder?.orderSession?.orderLines?.find(
        (line) => line.id === lineId
      );

    return refreshedLine?.unitItems || [];
  };

  const hasSingleCustomFieldSource = customFieldsData.length === 1;

  for (const customFieldsDataItem of customFieldsData) {
    const productId = customFieldsDataItem.productId;
    const orderLineId = (
      customFieldsDataItem as unknown as {
        orderLineId?: string;
      }
    ).orderLineId;

    const bookingFields = customFieldsDataItem.customFields.filter(
      (field) => (field.kind ?? CustomFieldKind.Order) === CustomFieldKind.Order
    );

    if (bookingFields.length) {
      const sortedBookingFields = [...bookingFields].sort(
        (a, b) => a.position - b.position
      );

      for (const field of sortedBookingFields) {
        const customField = toLegacyCustomField(
          field,
          productId,
          CustomFieldType.BOOKING
        );

        bookingAnswers.push({
          uuid: generateID(),
          bookingUuid: selectedOrder.id,
          pssUuids: [...orderLineIds],
          answer: "",
          listAnswer: [],
          createdAt: now,
          updatedAt: now,
          customField,
        });
      }
    }

    const ticketFields = customFieldsDataItem.customFields.filter(
      (field) =>
        (field.kind ?? CustomFieldKind.Order) === CustomFieldKind.OrderLine
    );

    if (!ticketFields.length) {
      continue;
    }

    const productIdKey =
      productId !== null && productId !== undefined ? String(productId) : null;
    const categoryLines =
      (productIdKey ? orderLinesByCategoryId.get(productIdKey) : undefined) ||
      [];

    const targetLinesBase = orderLineId
      ? orderLines.filter((line) => line.id === orderLineId)
      : [];

    const targetLines = targetLinesBase.length
      ? targetLinesBase
      : categoryLines.length
      ? categoryLines
      : hasSingleCustomFieldSource
      ? orderLines
      : [];

    if (!targetLines.length) {
      continue;
    }

    const sortedTicketFields = [...ticketFields].sort(
      (a, b) => a.position - b.position
    );

    for (const line of targetLines) {
      const unitItems = await getUnitItemsForLine(line.id);

      if (!unitItems.length) {
        continue;
      }

      for (const unitItem of unitItems) {
        for (const field of sortedTicketFields) {
          const customField = toLegacyCustomField(
            field,
            productId,
            CustomFieldType.TICKET
          );

          ticketAnswers.push({
            uuid: generateID(),
            bookingUuid: selectedOrder.id,
            pssUuid: unitItem.id,
            pssUuids: [unitItem.id],
            answer: "",
            listAnswer: [],
            createdAt: now,
            updatedAt: now,
            customField,
            priceId: line.priceId,
            priceName: line.priceName,
            categoryId: line.categoryId,
            categoryName: line.categoryName,
          });
        }
      }
    }
  }

  return {
    booking: bookingAnswers,
    ticket: ticketAnswers,
  };
}

/**
 * Get booking-level custom field answers for a specific order.
 *
 * @param {string} orderId - The ID of the order to fetch custom fields for.
 * @returns {Promise<ICustomFieldAnswer[]>} - A promise that resolves to an array of booking custom field answers.
 */
export async function getOrderBookingCustomFieldAnswers(
  orderId: string
): Promise<ICustomFieldAnswer[]> {
  const { booking } = await getOrderCustomFieldAnswers(orderId);
  return booking;
}

/**
 * Ensures that an order line has the correct number of unit items based on its
 * quantity. Creates missing unit items if the current count is less than the
 * quantity.
 *
 * @param {string} orderLineId - The ID of the order line to ensure unit items for.
 * @returns {Promise<void>} - A promise that resolves when all unit items are created.
 */
export async function ensureUnitItemsForOrderLine(
  orderLineId: string
): Promise<void> {
  const orderStore = useOrderStore();
  const order = orderStore.selectedOrder;

  if (!order) {
    throw new Error("No order selected");
  }

  const line = order.orderSession.orderLines.find((l) => l.id === orderLineId);

  if (!line) {
    throw new Error(`Order line with ID ${orderLineId} not found`);
  }

  if (line.unitItems?.length !== line.quantity) {
    const unitsToCreate = line.quantity - (line.unitItems?.length || 0);
    // One operation context for the whole backfill of this line.
    const ctx = newOperationContext();
    for (let i = 0; i < unitsToCreate; i++) {
      orderStore.addUnitItem(line.id, undefined, ctx);
    }
  }
}

/**
 * Ensures unit items exist for all order lines that have ticket-level custom
 * fields. Needed when editing orders so new unit items are created if a line's
 * quantity increased.
 *
 * @param {ICustomFieldAnswer[]} ticketCustomFields - Array of ticket-level custom field answers
 * @returns {Promise<void>} - A promise that resolves when all unit items are created.
 */
export async function ensureUnitItemsForTicketCustomFields(
  ticketCustomFields: ICustomFieldAnswer[]
): Promise<void> {
  const orderStore = useOrderStore();
  const order = orderStore.selectedOrder;

  if (!order || !ticketCustomFields.length) {
    return;
  }

  // Get unique order line IDs that have custom fields
  const orderLineIdsWithCustomFields = new Set<string>();

  for (const customField of ticketCustomFields) {
    if (customField.pssUuid) {
      // Find which order line contains this unit item
      const orderLine = order.orderSession.orderLines.find((line) =>
        line.unitItems?.some((unitItem) => unitItem.id === customField.pssUuid)
      );

      if (orderLine) {
        orderLineIdsWithCustomFields.add(orderLine.id);
      }
    }
  }

  // Ensure unit items for each order line that has custom fields
  for (const orderLineId of orderLineIdsWithCustomFields) {
    await ensureUnitItemsForOrderLine(orderLineId);
  }
}

/**
 * Get ticket-level custom field answers for a specific order.
 * Creates one answer per unit (individual ticket) for each custom field.
 * Each order line can have multiple units based on its quantity.
 *
 * @param {string} orderId - The ID of the order to fetch custom fields for.
 * @returns {Promise<ICustomFieldAnswer[]>} - A promise that resolves to an array of ticket custom field answers.
 */
export async function getOrderTicketCustomFieldAnswers(
  orderId: string
): Promise<ICustomFieldAnswer[]> {
  const { ticket } = await getOrderCustomFieldAnswers(orderId);
  return ticket;
}

/**
 * Maps order custom field answers to ICustomFieldAnswer format.
 *
 * @param customFieldAnswers - The array of custom field answers to map.
 * @returns A promise that resolves to an array of ICustomFieldAnswer objects.
 */
export async function mapOrderCustomFieldAnswersToICustomFieldAnswer(
  customFieldAnswers: CustomFieldAnswer[]
): Promise<ICustomFieldAnswer[]> {
  const orderStore = useOrderStore();
  const customFieldStore = useCustomFieldStore();
  const order = orderStore.selectedOrder;

  if (!order) {
    throw new Error("No order selected");
  }

  const customFieldsData = await customFieldStore.fetchOrderCustomFields(
    order.id
  );

  const customFieldMap = new Map<
    string,
    { field: CustomFieldDefinition; productId: number }
  >();

  customFieldsData.forEach((data) => {
    data.customFields.forEach((field) => {
      customFieldMap.set(String(field.fieldId), {
        field,
        productId: data.productId,
      });
    });
  });

  const orderLinesForAnswers = orderStore.allOrderLines;
  const findOrderLineForAnswer = (cfAnswer: CustomFieldAnswer) => {
    if (cfAnswer.orderLineId) {
      const byId = orderLinesForAnswers.find(
        (line) => line.id === cfAnswer.orderLineId
      );
      if (byId) {
        return byId;
      }
    }
    if (cfAnswer.unitItemId) {
      return orderLinesForAnswers.find((line) =>
        line.unitItems?.some((unitItem) => unitItem.id === cfAnswer.unitItemId)
      );
    }
    return undefined;
  };

  return customFieldAnswers.map((cfAnswer) => {
    const metadata = customFieldMap.get(cfAnswer.customFieldId);

    if (!metadata) {
      throw new Error(
        `Custom field with ID ${cfAnswer.customFieldId} not found for current order`
      );
    }

    const { field, productId } = metadata;
    const fieldKind = field.kind ?? CustomFieldKind.Order;
    const customFieldType =
      fieldKind === CustomFieldKind.OrderLine
        ? CustomFieldType.TICKET
        : CustomFieldType.BOOKING;

    const customField = toLegacyCustomField(field, productId, customFieldType);

    let answer = "";
    let listAnswer: string[] | string = [];

    if (cfAnswer.answer !== null) {
      if (Array.isArray(cfAnswer.answer)) {
        listAnswer = cfAnswer.answer;
        answer = cfAnswer.answer.join(",");
      } else {
        answer = cfAnswer.answer;
        if (
          customField.questionType === QuestionType.DROPDOWN ||
          customField.questionType === QuestionType.RADIO ||
          customField.questionType === QuestionType.LIST
        ) {
          listAnswer = cfAnswer.answer;
        }
      }
    }

    const isOrderAnswer = cfAnswer.type === CustomFieldAnswerType.Order;
    const orderLine = isOrderAnswer
      ? undefined
      : findOrderLineForAnswer(cfAnswer);

    return {
      uuid: cfAnswer.id,
      customFieldAnswerId: cfAnswer.id,
      bookingUuid: isOrderAnswer
        ? order.id
        : cfAnswer.unitItemId
        ? order.id
        : undefined,
      pssUuid:
        cfAnswer.type === CustomFieldAnswerType.OrderLine
          ? cfAnswer.unitItemId
          : undefined,
      pssUuids: isOrderAnswer
        ? []
        : cfAnswer.orderLineId
        ? [cfAnswer.orderLineId]
        : undefined,
      answer,
      listAnswer,
      createdAt: normalizeDateValue(cfAnswer.createdAt),
      updatedAt: normalizeDateValue(cfAnswer.updatedAt),
      customField,
      priceId: orderLine?.priceId,
      priceName: orderLine?.priceName,
      categoryId: orderLine?.categoryId,
      categoryName: orderLine?.categoryName,
    };
  });
}

/**
 * Map CustomFieldAnswer array to SyncCustomFieldAnswerDto array.
 *
 * @param {CustomFieldAnswer[]} customFieldAnswers
 * @returns {SyncCustomFieldAnswerDto[]}
 */
export function mapToSyncCustomFieldAnswerDtos(
  customFieldAnswers: CustomFieldAnswer[]
): SyncCustomFieldAnswerDto[] {
  return customFieldAnswers.map((answer) => {
    const syncDto: SyncCustomFieldAnswerDto = {
      customFieldAnswerId: answer.id,
      fieldId: answer.customFieldId,
      answer: answer.answer,
      orderSessionId: answer.orderSessionId,
    };

    if (answer.orderLineId) {
      syncDto.orderLineId = answer.orderLineId;
    }

    if (answer.unitItemId) {
      syncDto.unitItemId = answer.unitItemId;
    }

    return syncDto;
  });
}

/**
 * Extracts the file references (multipart key + blob id + display name) from a set
 * of custom-field answers. Serializable — safe to persist in the answer-sync queue
 * and rebuild the upload map later via {@link buildFilesMapFromRefs}.
 */
export function extractCustomFieldFileRefs(
  customFieldAnswers: CustomFieldAnswer[]
): IFileRef[] {
  return customFieldAnswers
    .filter((answer) => answer.file?.id && answer.customFieldId)
    .map((answer) => ({
      fileKey: `file_${answer.id}`,
      fileId: answer.file!.id,
      fileName:
        answer.file?.name ||
        (typeof answer.answer === "string" && answer.answer
          ? answer.answer
          : `file-${answer.file!.id}`),
    }));
}

/**
 * Rebuilds the `Record<string, File>` upload map from stored file refs by reading
 * each blob from `dexieDB.files`. Used both at completion and by the answer-sync
 * drain so offline-queued answers can be re-uploaded.
 */
export async function buildFilesMapFromRefs(
  fileRefs: IFileRef[]
): Promise<Record<string, File>> {
  if (!fileRefs.length) {
    return {};
  }

  const filesRecord: Record<string, File> = {};

  await Promise.all(
    fileRefs.map(async ({ fileKey, fileId, fileName }) => {
      if (filesRecord[fileKey]) {
        return;
      }

      try {
        const storedFile = await dexieDB.files.get(fileId);

        if (!storedFile?.file) {
          return;
        }

        const rawFile = storedFile.file;
        let fileToUpload: File | null = null;

        if (typeof File !== "undefined" && rawFile instanceof File) {
          fileToUpload = rawFile;
        } else if (typeof Blob !== "undefined" && rawFile instanceof Blob) {
          const options = rawFile.type ? { type: rawFile.type } : undefined;

          fileToUpload = new File([rawFile], fileName, options);
        } else if (rawFile instanceof ArrayBuffer) {
          fileToUpload = new File([rawFile], fileName);
        } else if (ArrayBuffer.isView(rawFile)) {
          const buffer =
            rawFile.buffer instanceof ArrayBuffer
              ? rawFile.buffer.slice(
                  rawFile.byteOffset,
                  rawFile.byteOffset + rawFile.byteLength
                )
              : undefined;

          if (buffer) {
            fileToUpload = new File([buffer], fileName);
          }
        } else if (
          rawFile &&
          typeof rawFile === "object" &&
          "data" in rawFile &&
          rawFile.data instanceof ArrayBuffer
        ) {
          fileToUpload = new File([rawFile.data], fileName);
        }

        if (!fileToUpload) {
          return;
        }

        filesRecord[fileKey] = fileToUpload;
      } catch (error) {
        console.warn("Unable to resolve custom field file for sync", {
          fileId,
          fileKey,
          error,
        });
      }
    })
  );

  return filesRecord;
}

export function getCustomFieldsFromCurrentOrderSession(
  customFields: CustomFieldsData[]
): CustomFieldsData[] {
  const orderStore = useOrderStore();
  const order = orderStore.selectedOrder;

  if (!order) {
    return [];
  }

  const orderLines = order.orderSession?.orderLines ?? [];
  const activeCategoryIds = new Set(
    orderLines
      .filter((line) => line.lineType !== LineType.CancelLine)
      .map((line) => line.categoryId)
      .filter((categoryId) => categoryId !== undefined && categoryId !== null)
      .map((categoryId) => String(categoryId))
  );

  if (!activeCategoryIds.size) {
    return [];
  }

  return customFields.filter((item) => {
    if (item.productId === undefined || item.productId === null) {
      return false;
    }

    return activeCategoryIds.has(String(item.productId));
  });
}
